type PaymentState =
  | 'connecting'
  | 'waiting_card'
  | 'accepted'
  | 'printing'
  | 'refused'
  | 'cancelled'
  | 'error';

interface PaymentDialogProps {
  state: PaymentState;
  error?: string;
  onClose?: () => void;
}

/* ---------- Icon components ---------- */

function SpinnerIcon() {
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" className="animate-spin text-accent">
      <circle cx="32" cy="32" r="28" fill="none" stroke="currentColor" strokeWidth="4" strokeOpacity="0.25" />
      <path
        d="M32 4a28 28 0 0 1 28 28"
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ContactlessIcon() {
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" className="text-accent">
      <path d="M32 44a12 12 0 0 0 0-24" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
      <path d="M32 36a4 4 0 0 0 0-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
      <path d="M32 52a20 20 0 0 0 0-40" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
      <rect x="8" y="22" width="20" height="28" rx="3" stroke="currentColor" strokeWidth="3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" className="text-success animate-pulse_icon">
      <circle cx="32" cy="32" r="28" fill="none" stroke="currentColor" strokeWidth="3" />
      <path d="M20 32l8 8 16-16" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

function PrinterIcon() {
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" className="text-accent">
      <rect x="16" y="8" width="32" height="16" rx="2" stroke="currentColor" strokeWidth="3" />
      <rect x="8" y="24" width="48" height="20" rx="4" stroke="currentColor" strokeWidth="3" />
      <rect x="16" y="40" width="32" height="16" rx="2" stroke="currentColor" strokeWidth="3" />
      <circle cx="46" cy="32" r="2" fill="currentColor" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" className="text-danger">
      <circle cx="32" cy="32" r="28" fill="none" stroke="currentColor" strokeWidth="3" />
      <path d="M22 22l20 20M42 22L22 42" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" className="text-warning">
      <path
        d="M32 6L4 58h56L32 6z"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <line x1="32" y1="26" x2="32" y2="40" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
      <circle cx="32" cy="48" r="2.5" fill="currentColor" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" className="text-danger">
      <circle cx="32" cy="32" r="28" fill="none" stroke="currentColor" strokeWidth="3" />
      <line x1="32" y1="18" x2="32" y2="38" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
      <circle cx="32" cy="46" r="2.5" fill="currentColor" />
    </svg>
  );
}

/* ---------- State config ---------- */

const STATE_CONFIG: Record<
  PaymentState,
  {
    icon: () => JSX.Element;
    label: string;
    sublabel?: string;
    button?: { text: string; className: string };
  }
> = {
  connecting: {
    icon: SpinnerIcon,
    label: 'Connexion au TPE...',
  },
  waiting_card: {
    icon: ContactlessIcon,
    label: 'Presentez votre carte',
    sublabel: 'Appuyez sur le bouton rouge pour annuler',
  },
  accepted: {
    icon: CheckIcon,
    label: 'Paiement accepte',
  },
  printing: {
    icon: PrinterIcon,
    label: 'Impression en cours...',
  },
  refused: {
    icon: CrossIcon,
    label: 'Paiement refuse',
    button: { text: 'Reessayer', className: 'btn-danger' },
  },
  cancelled: {
    icon: WarningIcon,
    label: 'Paiement annule',
    button: { text: 'Retour', className: 'btn-primary' },
  },
  error: {
    icon: AlertIcon,
    label: '',
    button: { text: 'Retour', className: 'btn-danger' },
  },
};

export function PaymentDialog({ state, error, onClose }: PaymentDialogProps) {
  const config = STATE_CONFIG[state];
  const Icon = config.icon;
  const label = state === 'error' ? error ?? 'Erreur inconnue' : config.label;

  return (
    <div className="overlay">
      <div className="bg-surface-card rounded-3xl p-8 w-[500px] max-w-[90vw] flex flex-col items-center gap-6 animate-fade-in">
        <Icon />

        <p className="text-2xl font-bold text-white text-center">{label}</p>

        {config.sublabel && (
          <p className="text-white/50 text-sm text-center">{config.sublabel}</p>
        )}

        {config.button && onClose && (
          <button
            type="button"
            className={`${config.button.className} w-full max-w-[280px] text-center`}
            onClick={onClose}
          >
            {config.button.text}
          </button>
        )}
      </div>
    </div>
  );
}
